import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Confetti from 'react-confetti';
import { QRCodeSVG } from 'qrcode.react';
import {
    SubmitLead,
    ValidateLeadForm,
    TrackMetric,
    GetPublicConfig,
    GetLocalCoupons,
    SaveCouponToLocal,
    RecoverCoupon,
    MaskUserData,
    LogScreenshot,
    MarkCouponScratched,
    CheckDuplicate
} from '../../../utilities/BL/OptimoOpticas';

const DEFAULT_TERMS = `TÉRMINOS Y CONDICIONES - RASCA Y GANA OPTIMO ÓPTICAS
1. Organizador: La dinámica "Rasca y Gana" es organizada por Optimo Ópticas en alianza estratégica con el Club Atlético de San Luis.
2. Proveedor Tecnológico: Five Digital Minds e Interaktia actúan exclusivamente como proveedores de la infraestructura técnica y quedan eximidos de la entrega física de bienes y servicios ópticos en sucursal.
3. Participación: Dinámica válida para aficionados y público general. Cada aficionado podrá participar y registrarse una sola vez con su número celular y correo electrónico único.
4. Asignación y Validez de Cupones: Cada cupón emitido contiene un folio alfanumérico único. El porcentaje o beneficio especificado aplica exclusivamente en los términos indicados y no es acumulable con otras promociones ni canjeable por dinero en efectivo.
5. Canje en Sucursal: Para hacer efectivo el beneficio, el titular deberá presentar el folio digital activo en su smartphone o captura de pantalla legible en la sucursal de Optimo Ópticas The Park.
6. Restricciones y Descalificación: Intentos de manipulación digital, duplicidad fraudulenta o uso de identidades falsas invalidarán de inmediato el cupón correspondiente.
7. Decisiones: Las resoluciones de Optimo Ópticas y el comité organizador respecto a la validez de los cupones son definitivas e inapelables.`;

const DEFAULT_PRIVACY = `AVISO DE PRIVACIDAD SIMPLIFICADO - OPTIMO ÓPTICAS
En estricto cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), sus datos personales (Nombre completo, Correo electrónico y Teléfono celular) serán tratados por Optimo Ópticas y la plataforma tecnológica Interaktia para las siguientes finalidades:
1. Validar su participación en la dinámica digital "Rasca y Gana".
2. Emitir, autenticar y salvaguardar el folio alfanumérico único de su cupón de descuento digital.
3. Brindarle atención, aclaraciones y seguimiento respecto a la vigencia y canje de su cupón.
4. Finalidad Secundaria (Opcional): Enviarle promociones exclusivas, noticias y beneficios de Optimo Ópticas, únicamente en caso de haber marcado la casilla de autorización correspondiente.

Sus datos personales están resguardados con estrictas medidas de seguridad técnicas, físicas y administrativas. En cualquier momento puede revocar su consentimiento o ejercer sus derechos ARCO (Acceso, Rectificación, Cancelación y Oposición) a través de los canales oficiales de atención al cliente de Optimo Ópticas.`;

const SOURCE_MAPPING = {
    obs: 'overlay_screen',
    seat: 'seats',
    bath: 'restrooms',
    qr: 'qr_flyer',
    stadium: 'stadium_screen',
    direct: 'direct_access'
};

export const OptimoOpticasFan = () => {
    const [searchParams] = useSearchParams();
    const rawSource = searchParams.get('s') || searchParams.get('utm_medium') || searchParams.get('src') || 'direct';
    const sourceParam = SOURCE_MAPPING[rawSource] || rawSource;

    // Máquina de estados: 'form' | 'scratch' | 'revealed'
    const [phase, setPhase] = useState('form');

    // Estado del Formulario
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        acceptedTerms: false,
        acceptedMarketing: false
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [formStartTracked, setFormStartTracked] = useState(false);
    const [duplicateDetected, setDuplicateDetected] = useState(false);

    // Configuración y Cupones
    const [config, setConfig] = useState(null);
    const [activeCoupon, setActiveCoupon] = useState(null);
    const activeCouponRef = useRef(activeCoupon);
    useEffect(() => {
        activeCouponRef.current = activeCoupon;
    }, [activeCoupon]);
    const [storedCoupons, setStoredCoupons] = useState([]);
    const [isSavingImage, setIsSavingImage] = useState(false);

    // Modales Legales
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // Modal de Recuperación Resiliente de Cupones (Rate Limiting y Anti-Bot)
    const [showRecoverModal, setShowRecoverModal] = useState(false);
    const [recoverQuery, setRecoverQuery] = useState('');
    const [recoverLoading, setRecoverLoading] = useState(false);
    const [recoverError, setRecoverError] = useState('');
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [botTrap, setBotTrap] = useState('');

    // Temporizador regresivo de Cooldown por Rate Limiting
    useEffect(() => {
        if (cooldownSeconds <= 0) return;
        const timer = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [cooldownSeconds]);

    // Animaciones y Confeti
    const [isRevealed, setIsRevealed] = useState(false);
    const [cardPulse, setCardPulse] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    // Referencias para Canvas y Muestreo Táctil
    const canvasRef = useRef(null);
    const fogRef = useRef(null);
    const cardWrapperRef = useRef(null);
    const isDrawingRef = useRef(false);
    const lastPosRef = useRef(null);
    const checkTimeoutRef = useRef(null);
    const hasInitializedCanvasRef = useRef(false);

    // Telemetría inicial: qr_scan (si viene de QR), page_view y carga de configuración
    useEffect(() => {
        const qrParam = searchParams.get('s') || (searchParams.get('src') === 'qr' ? 'qr' : null);
        if (qrParam) {
            TrackMetric('qr_scan', qrParam);
        }
        TrackMetric('page_view', sourceParam);

        const init = async () => {
            try {
                const local = GetLocalCoupons();
                if (local && local.length > 0) {
                    setStoredCoupons(local);
                }

                const res = await GetPublicConfig();
                if (res && res.success && res.data) {
                    setConfig(res.data);
                }
            } catch (err) {
                console.warn('Advertencia al inicializar OptimoOpticasFan:', err);
            }
        };

        init();
    }, [sourceParam]);

    // Detección de capturas de pantalla / cambio de pestaña en cupón revelado
    useEffect(() => {
        if (phase !== 'revealed' || !activeCoupon) return;

        const handleKeyUp = (e) => {
            if (e.key === 'PrintScreen' || e.keyCode === 44) {
                LogScreenshot(activeCoupon.participantId || null, 'KEYBOARD_SHORTCUT');
                TrackMetric('screenshot_saved', sourceParam);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                LogScreenshot(activeCoupon.participantId || null, 'HEURISTIC');
            }
        };

        window.addEventListener('keyup', handleKeyUp);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('keyup', handleKeyUp);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [phase, activeCoupon, sourceParam]);

    // Telemetría: form_start en primer foco
    const handleInputFocus = () => {
        if (!formStartTracked) {
            setFormStartTracked(true);
            TrackMetric('form_start', sourceParam);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Envío del formulario de registro
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setValidationErrors({});

        const validation = await ValidateLeadForm({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            acceptedTerms: formData.acceptedTerms,
            acceptedMarketing: formData.acceptedMarketing,
            trafficSource: sourceParam
        });

        if (!validation.isValid) {
            const errors = {};
            if (!formData.name || formData.name.trim().length < 3) {
                errors.name = 'Por favor ingresa tu nombre completo (mínimo 3 caracteres).';
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!formData.email || !emailRegex.test(formData.email.trim())) {
                errors.email = 'Por favor ingresa un correo electrónico válido.';
            }
            const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
            if (!cleanPhone || cleanPhone.length !== 10) {
                errors.phone = 'Por favor ingresa tu número celular a 10 dígitos.';
            }
            if (!formData.acceptedTerms) {
                errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones y el Aviso de Privacidad.';
            }
            if (Object.keys(errors).length === 0) {
                errors.general = validation.error || 'Por favor verifica los datos ingresados.';
            }
            setValidationErrors(errors);
            return;
        }

        TrackMetric('form_complete', sourceParam);
        setSubmitting(true);

        try {
            // 1. Verificación previa de duplicados por teléfono y/o correo
            const dupCheck = await CheckDuplicate(formData.phone, formData.email);
            if (dupCheck && dupCheck.exists) {
                setSubmitting(false);
                if (dupCheck.couponCode || dupCheck.isWinner) {
                    const existingCoupon = {
                        couponCode: dupCheck.couponCode || 'OPTI20',
                        discountDescription: config?.discountDescription || '2x1 en lentes Optimo', //'20% DE DESCUENTO en cualquier servicio.',
                        winnerRank: dupCheck.winnerRank,
                        verificationToken: dupCheck.verificationToken,
                        additionalInstructions: config?.additionalInstructions || 'Muestra este cupón en la sucursal de The Park para hacerlo válido.',
                        showPassQr: config?.showPassQr ?? true,
                        showPassUserData: config?.showPassUserData ?? false,
                        showPassLocationNote: config?.showPassLocationNote ?? true,
                        name: dupCheck.name || formData.name,
                        email: dupCheck.email || formData.email,
                        phone: dupCheck.phone || formData.phone,
                        participantId: dupCheck.participantId,
                        isDuplicate: true,
                        storageExpirationDays: config?.storageExpirationDays || 7
                    };
                    SaveCouponToLocal(existingCoupon);
                    const updated = GetLocalCoupons(existingCoupon.storageExpirationDays);
                    setStoredCoupons(updated);
                    setActiveCoupon(existingCoupon);
                    setIsRevealed(true);
                    setPhase('revealed');
                    return;
                }
                // Si ya participó pero no fue ganador de cupón
                setPhase('already_participated');
                return;
            }

            // 2. Procesar registro si no fue detectado duplicado previo
            const response = await SubmitLead({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                acceptedTerms: formData.acceptedTerms,
                acceptedMarketing: formData.acceptedMarketing,
                trafficSource: sourceParam
            });

            setSubmitting(false);

            // 3. Si el backend detectó duplicidad al intentar guardar (por concurrencia o índice)
            if (response && response.isDuplicate) {
                if (response.couponCode || response.isWinner) {
                    const existingCoupon = {
                        couponCode: response.couponCode || 'OPTI20',
                        discountDescription: response.discountDescription || config?.discountDescription || '2x1 en lentes Optimo.', //'20% DE DESCUENTO en cualquier servicio.',
                        winnerRank: response.winnerRank,
                        verificationToken: response.verificationToken,
                        additionalInstructions: response.additionalInstructions || config?.additionalInstructions || 'Muestra este cupón en la sucursal de The Park para hacerlo válido.',
                        showPassQr: response.showPassQr !== undefined ? response.showPassQr : (config?.showPassQr ?? true),
                        showPassUserData: response.showPassUserData !== undefined ? response.showPassUserData : (config?.showPassUserData ?? false),
                        showPassLocationNote: response.showPassLocationNote !== undefined ? response.showPassLocationNote : (config?.showPassLocationNote ?? true),
                        name: response.name || formData.name,
                        email: response.email || formData.email,
                        phone: response.phone || formData.phone,
                        participantId: response.participantId,
                        isDuplicate: true,
                        storageExpirationDays: response.storageExpirationDays || config?.storageExpirationDays || 7
                    };
                    SaveCouponToLocal(existingCoupon);
                    const updated = GetLocalCoupons(existingCoupon.storageExpirationDays);
                    setStoredCoupons(updated);
                    setActiveCoupon(existingCoupon);
                    setIsRevealed(true);
                    setPhase('revealed');
                    return;
                }
                // Si ya participó pero no fue ganador de cupón
                setPhase('already_participated');
                return;
            }

            // 4. Participante nuevo que obtuvo cupón: pasar al raspadito
            if (response && response.success && (response.isWinner || response.couponCode)) {
                const couponInfo = {
                    couponCode: response.couponCode || 'OPTI20',
                    discountDescription: response.discountDescription || config?.discountDescription || '2x1 en lentes Optimo.', //'20% DE DESCUENTO en cualquier servicio.',
                    winnerRank: response.winnerRank,
                    verificationToken: response.verificationToken,
                    additionalInstructions: response.additionalInstructions || config?.additionalInstructions || 'Muestra este cupón en la sucursal de The Park para hacerlo válido.',
                    showPassQr: response.showPassQr !== undefined ? response.showPassQr : (config?.showPassQr ?? true),
                    showPassUserData: response.showPassUserData !== undefined ? response.showPassUserData : (config?.showPassUserData ?? false),
                    showPassLocationNote: response.showPassLocationNote !== undefined ? response.showPassLocationNote : (config?.showPassLocationNote ?? true),
                    name: response.name || formData.name,
                    email: response.email || formData.email,
                    phone: response.phone || formData.phone,
                    participantId: response.participantId,
                    isDuplicate: false,
                    storageExpirationDays: response.storageExpirationDays || config?.storageExpirationDays || 7
                };

                SaveCouponToLocal(couponInfo);
                setActiveCoupon(couponInfo);
                const updated = GetLocalCoupons(couponInfo.storageExpirationDays);
                setStoredCoupons(updated);

                // Transición inmediata a la fase 'scratch'
                TrackMetric('game_start', sourceParam);
                hasInitializedCanvasRef.current = false;
                setIsRevealed(false);
                setPhase('scratch');
            } else if (response && response.success && response.isWinner === false) {
                // Límite de ganadores alcanzado (modo limitado)
                setValidationErrors({
                    general: response.message || 'Se ha alcanzado el límite de cupones para esta dinámica. ¡Gracias por participar!'
                });
            } else {
                setValidationErrors({
                    general: response?.message || 'Ocurrió un error al procesar tu registro. Por favor intenta nuevamente.'
                });
            }
        } catch (error) {
            console.error('Error al enviar registro:', error);
            setSubmitting(false);
            setValidationErrors({
                general: 'No se pudo conectar con el servidor. Revisa tu conexión a internet.'
            });
        }
    };

    // =========================================================================
    // MECÁNICA DEL CANVAS DE RASCA Y GANA
    // =========================================================================

    const triggerReveal = useCallback(() => {
        if (isRevealed) return;
        setIsRevealed(true);
        TrackMetric('game_complete', sourceParam);

        // Notificar al backend que el cupón fue descubierto
        const currentCoupon = activeCouponRef.current || activeCoupon;
        if (currentCoupon) {
            MarkCouponScratched(currentCoupon.participantId || null, currentCoupon.couponCode || null);
        }

        // 1. Fundido suave de canvas y niebla (700ms)
        if (canvasRef.current) {
            canvasRef.current.style.opacity = '0';
        }
        if (fogRef.current) {
            fogRef.current.style.opacity = '0';
        }

        // 2. Pulso dinámico en la tarjeta ('scale-105')
        setCardPulse(true);
        setTimeout(() => {
            setCardPulse(false);
        }, 400);

        // 3. Explosión de confeti multicolor
        setShowConfetti(true);
        setTimeout(() => {
            setShowConfetti(false);
        }, 7000);

        // 4. Transición final de estado tras 700ms
        setTimeout(() => {
            setPhase('revealed');
        }, 700);
    }, [isRevealed, activeCoupon, sourceParam]);

    // Muestreo eficiente a 150 ms para cálculo de porcentaje descubierto sin sobrecalentar GPU
    const checkReveal = useCallback(() => {
        if (checkTimeoutRef.current || isRevealed) return;

        checkTimeoutRef.current = setTimeout(() => {
            const canvas = canvasRef.current;
            if (!canvas || isRevealed) {
                checkTimeoutRef.current = null;
                return;
            }

            try {
                const ctx = canvas.getContext('2d');
                const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                let transparentPixels = 0;
                let totalSampled = 0;

                // Paso de muestreo de 16 bytes (1 de cada 4 píxeles) para máximo rendimiento táctil
                for (let i = 3; i < pixels.length; i += 16) {
                    totalSampled++;
                    if (pixels[i] === 0) {
                        transparentPixels++;
                    }
                }

                const clearedPercentage = totalSampled > 0 ? (transparentPixels / totalSampled) * 100 : 0;

                if (clearedPercentage > 65) {
                    triggerReveal();
                }
            } catch (err) {
                console.warn('Error en muestreo de canvas:', err);
            }

            checkTimeoutRef.current = null;
        }, 150);
    }, [isRevealed, triggerReveal]);

    const getCanvasCoordinates = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / (rect.width || 1);
        const scaleY = canvas.height / (rect.height || 1);
        const clientX = e.touches && e.touches[0] ? e.touches[0].clientX : (e.clientX ?? 0);
        const clientY = e.touches && e.touches[0] ? e.touches[0].clientY : (e.clientY ?? 0);
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    }, []);

    const performScratch = useCallback((startPos, endPos) => {
        const canvas = canvasRef.current;
        if (!canvas || isRevealed) return;
        const ctx = canvas.getContext('2d');

        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';

        // Borrado circular con radio 45px en el punto actual
        ctx.beginPath();
        ctx.arc(endPos.x, endPos.y, 45, 0, Math.PI * 2);
        ctx.fill();

        // Trazo suave continuo con ancho 90px (radio 45px) entre puntos
        if (startPos) {
            ctx.beginPath();
            ctx.lineWidth = 90;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.moveTo(startPos.x, startPos.y);
            ctx.lineTo(endPos.x, endPos.y);
            ctx.stroke();
        }

        ctx.restore();
        checkReveal();
    }, [isRevealed, checkReveal]);

    // Inicialización del Lienzo Canvas cuando entra a fase 'scratch'
    useEffect(() => {
        if (phase !== 'scratch' || hasInitializedCanvasRef.current) return;

        const timer = setTimeout(() => {
            const canvas = canvasRef.current;
            const wrapper = cardWrapperRef.current;
            if (!canvas || !wrapper) return;

            const rect = wrapper.getBoundingClientRect();
            canvas.width = Math.round(rect.width) || 360;
            canvas.height = Math.round(rect.height) || 480;

            const ctx = canvas.getContext('2d');

            // 1. Textura metálica gris punteada (#64748b)
            ctx.fillStyle = '#64748b';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < 2000; i++) {
                ctx.fillStyle = Math.random() > 0.5 ? '#475569' : '#94a3b8';
                ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
            }

            // 2. Dibuja la mitad superior de '/img/optimoopticas/meme.webp'
            const img = new Image();
            img.src = '/img/optimoopticas/meme.webp';

            const drawMemeAndText = () => {
                const size = Math.min(180, Math.round(canvas.width * 0.48));
                const textSpace = 75;
                const totalHeight = size + textSpace;

                const x = (canvas.width - size) / 2;
                const y = (canvas.height - totalHeight) / 2 - 10;

                try {
                    // Extraer mitad superior de la imagen
                    ctx.drawImage(img, 0, 0, img.width, img.height / 2, x, y, size, size);
                } catch (e) {
                    // Fallback si falla la decodificación
                    ctx.fillStyle = '#124dde';
                    ctx.fillRect(x, y, size, size);
                }

                // Enmarcada en azul (#124dde, 4px)
                ctx.strokeStyle = '#124dde';
                ctx.lineWidth = 4;
                ctx.strokeRect(x, y, size, size);

                // Texto centrado: 'FROTA AQUÍ PARA OBTENER TU REGALO'
                const fontSize = canvas.width < 360 ? 17 : 20;
                ctx.font = `900 ${fontSize}px Montserrat, sans-serif`;
                ctx.fillStyle = '#124dde';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Contorno blanco nítido
                ctx.lineWidth = 4;
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';

                const line1Y = y + size + 32;
                const line2Y = y + size + 58;

                ctx.strokeText('FROTA AQUÍ PARA', canvas.width / 2, line1Y);
                ctx.fillText('FROTA AQUÍ PARA', canvas.width / 2, line1Y);

                ctx.strokeText('OBTENER TU REGALO', canvas.width / 2, line2Y);
                ctx.fillText('OBTENER TU REGALO', canvas.width / 2, line2Y);
            };

            if (img.complete && img.naturalWidth > 0) {
                drawMemeAndText();
            } else {
                img.onload = drawMemeAndText;
                img.onerror = drawMemeAndText;
            }

            hasInitializedCanvasRef.current = true;
        }, 60);

        return () => clearTimeout(timer);
    }, [phase]);

    // Bloqueo total de gestos nativos de navegación (swipe back / pull-to-refresh) y scroll durante el raspado
    useEffect(() => {
        if (phase !== 'scratch') return;

        const originalBodyOverflow = document.body.style.overflow;
        const originalBodyTouchAction = document.body.style.touchAction;
        const originalBodyOverscroll = document.body.style.overscrollBehavior;
        const originalHtmlOverscroll = document.documentElement.style.overscrollBehavior;

        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';

        // Prevenir gestos de swipe y arrastre a nivel de ventana si el dedo se sale ligeramente del lienzo
        const preventWindowGestures = (e) => {
            if (e.cancelable) {
                e.preventDefault();
            }
        };

        window.addEventListener('touchmove', preventWindowGestures, { passive: false });
        window.addEventListener('gesturestart', preventWindowGestures, { passive: false });
        window.addEventListener('gesturechange', preventWindowGestures, { passive: false });

        return () => {
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.touchAction = originalBodyTouchAction;
            document.body.style.overscrollBehavior = originalBodyOverscroll;
            document.documentElement.style.overscrollBehavior = originalHtmlOverscroll;

            window.removeEventListener('touchmove', preventWindowGestures);
            window.removeEventListener('gesturestart', preventWindowGestures);
            window.removeEventListener('gesturechange', preventWindowGestures);
        };
    }, [phase]);

    // Vinculación de eventos táctiles unificados con exclusividad sobre gestos del navegador
    useEffect(() => {
        if (phase !== 'scratch') return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const handlePointerDown = (e) => {
            if (isRevealed) return;
            if (e.cancelable) e.preventDefault();
            isDrawingRef.current = true;
            const pos = getCanvasCoordinates(e);
            lastPosRef.current = pos;
            performScratch(null, pos);
            try {
                if (canvas.setPointerCapture) {
                    canvas.setPointerCapture(e.pointerId);
                }
            } catch (_) {}
        };

        const handlePointerMove = (e) => {
            if (!isDrawingRef.current || isRevealed) return;
            if (e.cancelable) e.preventDefault();
            const pos = getCanvasCoordinates(e);
            performScratch(lastPosRef.current, pos);
            lastPosRef.current = pos;
        };

        const handlePointerUp = (e) => {
            isDrawingRef.current = false;
            lastPosRef.current = null;
            try {
                if (canvas.hasPointerCapture && canvas.hasPointerCapture(e.pointerId)) {
                    canvas.releasePointerCapture(e.pointerId);
                }
            } catch (_) {}
        };

        const handleTouchPrevent = (e) => {
            if (e.cancelable) {
                e.preventDefault();
            }
        };

        canvas.addEventListener('pointerdown', handlePointerDown, { passive: false });
        canvas.addEventListener('pointermove', handlePointerMove, { passive: false });
        canvas.addEventListener('pointerup', handlePointerUp);
        canvas.addEventListener('pointercancel', handlePointerUp);
        canvas.addEventListener('pointerleave', handlePointerUp);
        canvas.addEventListener('touchstart', handleTouchPrevent, { passive: false });
        canvas.addEventListener('touchmove', handleTouchPrevent, { passive: false });

        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            canvas.removeEventListener('pointermove', handlePointerMove);
            canvas.removeEventListener('pointerup', handlePointerUp);
            canvas.removeEventListener('pointercancel', handlePointerUp);
            canvas.removeEventListener('pointerleave', handlePointerUp);
            canvas.removeEventListener('touchstart', handleTouchPrevent);
            canvas.removeEventListener('touchmove', handleTouchPrevent);
        };
    }, [phase, isRevealed, getCanvasCoordinates, performScratch]);

    // =========================================================================
    // MANEJADOR RESILIENTE DE RECUPERACIÓN DE CUPONES (RATE LIMITING + HONEYPOT)
    // =========================================================================
    const handleRecoverSubmit = async (e) => {
        e.preventDefault();

        // 1. Detección de trampa anti-bot (Honeypot invisible)
        if (botTrap) {
            setRecoverError('Petición no permitida.');
            return;
        }

        // 2. Control de Cooldown activo por Rate Limiting
        if (cooldownSeconds > 0) {
            setRecoverError(`Por favor espera ${cooldownSeconds} segundos antes de realizar una nueva consulta.`);
            return;
        }

        if (!recoverQuery || !recoverQuery.trim()) {
            setRecoverError('Por favor ingresa tu correo o número telefónico registrado.');
            return;
        }

        setRecoverLoading(true);
        setRecoverError('');

        try {
            const result = await RecoverCoupon(recoverQuery.trim());
            setRecoverLoading(false);

            if (result && result.isRateLimited) {
                const wait = result.waitSeconds || 60;
                setCooldownSeconds(wait);
                setRecoverError(result.message || `Límite de consultas excedido. Por favor espera ${wait} segundos antes de reintentar.`);
                return;
            }

            if (!result || !result.success || !result.isWinner || !result.couponCode) {
                setRecoverError(result?.message || 'No se encontró ningún cupón activo asociado a los datos ingresados.');
                return;
            }

            const couponData = {
                couponCode: result.couponCode,
                discountDescription: result.discountDescription || config?.discountDescription || '2x1 en lentes Optimo.', //'20% DE DESCUENTO en cualquier servicio.',
                winnerRank: result.winnerRank,
                name: result.name,
                email: result.email,
                phone: result.phone,
                verificationToken: result.verificationToken,
                matchTitle: result.matchTitle,
                campaignTitle: result.campaignTitle,
                showPassUserData: result.showPassUserData !== undefined ? result.showPassUserData : (config?.showPassUserData ?? false),
                showPassQr: result.showPassQr !== undefined ? result.showPassQr : (config?.showPassQr ?? true),
                showPassLocationNote: result.showPassLocationNote !== undefined ? result.showPassLocationNote : (config?.showPassLocationNote ?? true),
                additionalInstructions: result.additionalInstructions || config?.additionalInstructions || 'Muestra este cupón en la sucursal de The Park para hacerlo válido.',
                participantId: result.participantId || null,
                storageExpirationDays: result.storageExpirationDays || config?.storageExpirationDays || 7
            };

            SaveCouponToLocal(couponData);
            const updated = GetLocalCoupons(couponData.storageExpirationDays);
            setStoredCoupons(updated);
            setActiveCoupon(couponData);
            setShowRecoverModal(false);
            TrackMetric('coupon_recovered', sourceParam);
            setPhase('revealed');
        } catch (error) {
            console.error('Error al recuperar cupón:', error);
            setRecoverLoading(false);
            setRecoverError('Error de conexión al consultar el cupón. Por favor intenta de nuevo.');
        }
    };

    // =========================================================================
    // GENERACIÓN NATIVA DE IMAGEN PNG EN ALTA DEFINICIÓN (720PX) Y GUARDADO
    // =========================================================================
    const handleSavePassToGallery = async () => {
        if (!activeCoupon || isSavingImage) return;

        setIsSavingImage(true);
        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const width = 720;
            const cardMargin = 30;
            const cardW = width - (cardMargin * 2);

            const hasQr = activeCoupon?.showPassQr !== undefined 
                ? activeCoupon.showPassQr 
                : (config?.showPassQr ?? true);

            const hasUserData = activeCoupon?.showPassUserData !== undefined 
                ? activeCoupon.showPassUserData 
                : (config?.showPassUserData ?? false);

            const hasLocationNote = activeCoupon?.showPassLocationNote !== undefined 
                ? activeCoupon.showPassLocationNote 
                : (config?.showPassLocationNote ?? true);

            // Cálculo dinámico de altura para mantener proporciones estéticas
            let calculatedH = 430;
            if (hasQr) calculatedH += 250;
            if (hasUserData) calculatedH += 65;
            if (hasLocationNote) calculatedH += 75;
            calculatedH += 65;

            const height = calculatedH;
            canvas.width = width;
            canvas.height = height;

            // 1. Fondo exterior azul oficial #124dde
            ctx.fillStyle = '#124dde';
            ctx.fillRect(0, 0, width, height);

            // 2. Tarjeta blanca con borde azul (#124dde, 6px) y esquinas redondeadas
            const cardH = height - (cardMargin * 2);
            const radius = 28;

            ctx.save();
            ctx.beginPath();
            if (typeof ctx.roundRect === 'function') {
                ctx.roundRect(cardMargin, cardMargin, cardW, cardH, radius);
            } else {
                ctx.rect(cardMargin, cardMargin, cardW, cardH);
            }
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 6;
            ctx.strokeStyle = '#124dde';
            ctx.stroke();
            ctx.clip();

            let currentY = cardMargin + 25;

            // 3. Logo oficial '/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png'
            const loadLocalImage = (src) => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(null);
                    img.src = src;
                });
            };

            const logoImg = (await loadLocalImage('/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png')) || (await loadLocalImage('/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo'));
            if (logoImg) {
                const logoH = 48;
                const ratio = (logoImg.width || 1) / (logoImg.height || 1);
                const logoW = Math.min(230, logoH * ratio);
                const padX = 14;
                const padY = 8;
                const containerW = logoW + (padX * 2);
                const containerH = logoH + (padY * 2);
                const containerX = (width - containerW) / 2;
                const containerY = currentY;
                const logoRadius = 12;

                ctx.save();
                ctx.beginPath();
                if (typeof ctx.roundRect === 'function') {
                    ctx.roundRect(containerX, containerY, containerW, containerH, logoRadius);
                } else {
                    ctx.rect(containerX, containerY, containerW, containerH);
                }
                ctx.fillStyle = '#124dde';
                ctx.fill();
                ctx.clip();

                ctx.drawImage(logoImg, containerX + padX, containerY + padY, logoW, logoH);
                ctx.restore();

                currentY += containerH + 16;
            } else {
                currentY += 20;
            }

            // 4. Título '¡FELICIDADES!'
            ctx.textAlign = 'center';
            ctx.fillStyle = '#124dde';
            ctx.font = '900 38px Montserrat, Arial, sans-serif';
            ctx.fillText('¡FELICIDADES!', width / 2, currentY + 32);
            currentY += 45;

            // 5. Divisor con degradado azul
            const divGrad = ctx.createLinearGradient(cardMargin + 40, currentY, width - cardMargin - 40, currentY);
            divGrad.addColorStop(0, 'transparent');
            divGrad.addColorStop(0.5, '#124dde');
            divGrad.addColorStop(1, 'transparent');
            ctx.fillStyle = divGrad;
            ctx.fillRect(cardMargin + 40, currentY, cardW - 80, 2);
            currentY += 18;

            // 6. Texto del descuento
            ctx.fillStyle = '#334155';
            ctx.font = '600 17px Montserrat, Arial, sans-serif';
            ctx.fillText('¡Ya ganaste!', width / 2, currentY + 14);
            currentY += 22;

            ctx.fillStyle = '#124dde';
            // ctx.font = '900 32px Montserrat, Arial, sans-serif';
            ctx.font = '900 17px Montserrat, Arial, sans-serif';
            // const rawDiscount = activeCoupon?.discountDescription || config?.discountDescription || '20% DE DESCUENTO';
            // const discountTitle = rawDiscount.includes('20%') ? '20% DE DESCUENTO' : rawDiscount;
            const discountTitle = '2x1 en lentes Optimo';
            ctx.fillText(discountTitle, width / 2, currentY + 28);
            currentY += 36;

            ctx.fillStyle = '#334155';
            ctx.font = '600 16px Montserrat, Arial, sans-serif';
            // const discountSub = rawDiscount.includes('20%') ? 'en cualquier servicio.' : 'en Optimo Ópticas';
            //const discountSub = 'Tu premio te espera en Optimo Ópticas The Park. Ven y conoce los beneficios exclusivos que tenemos para ti.';
            ctx.fillText('Tu premio te espera en Optimo Ópticas The Park.', width / 2, currentY + 16);
            currentY += 30;
            ctx.fillText('Ven y conoce los beneficios exclusivos que tenemos para ti.', width / 2, currentY + 16);
            currentY += 30;

            // 7. Banner shimmer metálico con el folio Crockford (#OPTI-...)
            const bannerX = 55;
            const bannerW = width - 110;
            const bannerH = 120;
            const bannerY = currentY;

            const bannerGrad = ctx.createLinearGradient(bannerX, bannerY, bannerX + bannerW, bannerY + bannerH);
            bannerGrad.addColorStop(0, '#0e3eb5');
            bannerGrad.addColorStop(0.5, '#124dde');
            bannerGrad.addColorStop(1, '#0e3eb5');

            ctx.beginPath();
            if (typeof ctx.roundRect === 'function') {
                ctx.roundRect(bannerX, bannerY, bannerW, bannerH, 14);
            } else {
                ctx.rect(bannerX, bannerY, bannerW, bannerH);
            }
            ctx.fillStyle = bannerGrad;
            ctx.fill();

            ctx.fillStyle = '#ffffff';
            ctx.font = '900 22px Montserrat, Arial, sans-serif';
            ctx.fillText('CUPÓN EXCLUSIVO', width / 2, bannerY + 36);

            // Badge para el folio
            const badgeW = 320;
            const badgeH = 46;
            const badgeX = (width - badgeW) / 2;
            const badgeY = bannerY + 54;

            ctx.beginPath();
            if (typeof ctx.roundRect === 'function') {
                ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 10);
            } else {
                ctx.rect(badgeX, badgeY, badgeW, badgeH);
            }
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#bfdbfe';
            ctx.stroke();

            ctx.fillStyle = '#124dde';
            ctx.font = '900 24px monospace, "Courier New", Courier';
            const folioStr = activeCoupon?.couponCode ? (activeCoupon.couponCode.startsWith('#') ? activeCoupon.couponCode : `#${activeCoupon.couponCode}`) : '#OPTI20';
            ctx.fillText(folioStr, width / 2, badgeY + 31);

            currentY = bannerY + bannerH + 16;

            // 8. Texto 'Presenta este cupón en caja' antes del QR
            ctx.fillStyle = '#64748b';
            ctx.font = '600 13px Montserrat, Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Presenta este cupón en caja', width / 2, currentY + 12);
            currentY += 26;

            // 9. Código QR extraído del SVG en el DOM (si hasQr)
            if (hasQr) {
                const qrContainerSize = 200;
                const qrContainerX = (width - qrContainerSize) / 2;
                const qrContainerY = currentY;

                // Contenedor azul oficial #124dde con esquinas redondeadas
                ctx.beginPath();
                if (typeof ctx.roundRect === 'function') {
                    ctx.roundRect(qrContainerX, qrContainerY, qrContainerSize, qrContainerSize, 18);
                } else {
                    ctx.rect(qrContainerX, qrContainerY, qrContainerSize, qrContainerSize);
                }
                ctx.fillStyle = '#124dde';
                ctx.fill();

                // Recuadro interior blanco para zona segura del QR
                const innerPad = 12;
                const innerSize = qrContainerSize - (innerPad * 2);
                ctx.beginPath();
                if (typeof ctx.roundRect === 'function') {
                    ctx.roundRect(qrContainerX + innerPad, qrContainerY + innerPad, innerSize, innerSize, 10);
                } else {
                    ctx.rect(qrContainerX + innerPad, qrContainerY + innerPad, innerSize, innerSize);
                }
                ctx.fillStyle = '#ffffff';
                ctx.fill();

                const qrSvgElement = document.querySelector('#coupon-qr-container svg') || document.querySelector('#scratch-pass-qr svg') || document.querySelector('#pass-card svg') || document.querySelector('.OptimoOpticasFan svg');
                if (qrSvgElement) {
                    const svgData = new XMLSerializer().serializeToString(qrSvgElement);
                    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                    const svgUrl = URL.createObjectURL(svgBlob);
                    const qrImg = new Image();

                    await new Promise((resolve) => {
                        qrImg.onload = () => {
                            const qrDrawPad = 6;
                            ctx.drawImage(qrImg, qrContainerX + innerPad + qrDrawPad, qrContainerY + innerPad + qrDrawPad, innerSize - (qrDrawPad * 2), innerSize - (qrDrawPad * 2));
                            URL.revokeObjectURL(svgUrl);
                            resolve();
                        };
                        qrImg.onerror = () => {
                            URL.revokeObjectURL(svgUrl);
                            resolve();
                        };
                        qrImg.src = svgUrl;
                    });
                }

                // Dibujar el logo primario cuadrado en el centro del QR en el canvas generado
                const qrCenterLogo = await loadLocalImage('/img/optimoopticas/logo-optimo-opticas-primario-cuadrado.png');
                if (qrCenterLogo) {
                    const logoSz = 38;
                    const logoX = qrContainerX + ((qrContainerSize - logoSz) / 2);
                    const logoY = qrContainerY + ((qrContainerSize - logoSz) / 2);
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(logoX - 2, logoY - 2, logoSz + 4, logoSz + 4);
                    ctx.drawImage(qrCenterLogo, logoX, logoY, logoSz, logoSz);
                }

                currentY += qrContainerSize + 24;
            }

            // 9. Datos personales enmascarados si hasUserData está activo
            if (hasUserData) {
                const masked = MaskUserData(activeCoupon?.name || '', activeCoupon?.email || '', activeCoupon?.phone || '');
                ctx.fillStyle = '#0f172a';
                ctx.font = '800 19px Montserrat, Arial, sans-serif';
                ctx.fillText(masked.name || 'Aficionado Registrado', width / 2, currentY + 16);

                ctx.fillStyle = '#64748b';
                ctx.font = '600 14px Montserrat, Arial, sans-serif';
                const sub = masked.phone ? `${masked.email} | ${masked.phone}` : masked.email;
                ctx.fillText(sub || '', width / 2, currentY + 38);

                currentY += 55;
            }

            // 10. Recuadro de indicaciones si hasLocationNote está activo
            if (hasLocationNote) {
                const secBoxX = 55;
                const secBoxY = currentY;
                const secBoxW = width - 110;
                const secBoxH = 50;

                ctx.beginPath();
                if (typeof ctx.roundRect === 'function') {
                    ctx.roundRect(secBoxX, secBoxY, secBoxW, secBoxH, 10);
                } else {
                    ctx.rect(secBoxX, secBoxY, secBoxW, secBoxH);
                }
                ctx.fillStyle = '#eff6ff';
                ctx.fill();
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = '#bfdbfe';
                ctx.stroke();

                ctx.fillStyle = '#124dde';
                ctx.font = '900 13px Montserrat, Arial, sans-serif';
                const noteText = activeCoupon?.additionalInstructions || config?.additionalInstructions || 'MUESTRA ESTE CUPÓN EN SUCURSAL PARA HACERLO VÁLIDO';
                ctx.fillText(noteText, width / 2, secBoxY + 30);

                currentY += secBoxH + 18;
            }

            // 11. Sección institucional inferior
            // ctx.fillStyle = '#94a3b8';
            // ctx.font = '700 12px Montserrat, Arial, sans-serif';
            // ctx.fillText('OPTIMO ÓPTICAS • ATLÉTICO DE SAN LUIS', width / 2, currentY + 14);

            ctx.restore();

            // 12. Convertir canvas a blob PNG y disparar descarga
            canvas.toBlob((blob) => {
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                const cleanFolio = (activeCoupon?.couponCode || 'OPTI20').replace(/[^a-zA-Z0-9]/g, '');
                a.download = `Cupon_OptimoOpticas_${cleanFolio}.png`;
                a.href = url;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 'image/png');

            // Auditoría y Telemetría
            await LogScreenshot(activeCoupon.participantId || null, 'BUTTON_DOWNLOAD');
            TrackMetric('screenshot_saved', sourceParam);
        } catch (err) {
            console.error('Error al guardar cupón en galería:', err);
        } finally {
            setIsSavingImage(false);
        }
    };

    // Renderizado de Modales Legales LFPDPPP
    const renderLegalModal = (title, content, setter) => {
        return (
            <div className="OptimoOpticasFan__LegalModalBackdrop" onClick={() => setter(false)}>
                <div className="OptimoOpticasFan__LegalModalCard" onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                        <h4 className="fw-bold text-dark mb-0 fs-5">{title}</h4>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Cerrar"
                            onClick={() => setter(false)}
                        ></button>
                    </div>
                    <div
                        className="text-secondary small"
                        style={{
                            whiteSpace: 'pre-wrap',
                            textAlign: 'justify',
                            lineHeight: '1.6',
                            maxHeight: '55vh',
                            overflowY: 'auto'
                        }}
                    >
                        {content}
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary w-100 py-2.5 rounded-3 fw-bold mt-4"
                        style={{ backgroundColor: '#124dde', borderColor: '#124dde' }}
                        onClick={() => setter(false)}
                    >
                        Entendido / Cerrar
                    </button>
                </div>
            </div>
        );
    };

    const showQr = activeCoupon?.showPassQr !== undefined 
        ? activeCoupon.showPassQr 
        : (config?.showPassQr ?? true);

    const showUserData = activeCoupon?.showPassUserData !== undefined 
        ? activeCoupon.showPassUserData 
        : (config?.showPassUserData ?? false);

    const showLocationNote = activeCoupon?.showPassLocationNote !== undefined 
        ? activeCoupon.showPassLocationNote 
        : (config?.showPassLocationNote ?? true);

    const maskedUserData = MaskUserData(
        activeCoupon?.name || '',
        activeCoupon?.email || '',
        activeCoupon?.phone || ''
    );

    return (
        <div className={`OptimoOpticasFan OptimoOpticasFan--${phase}`}>
            {/* Confeti Multicolor de Celebración */}
            {showConfetti && (
                <Confetti
                    numberOfPieces={220}
                    recycle={false}
                    colors={['#124dde', '#0e3eb5', '#ffffff', '#fbbf24', '#fcd34d', '#38bdf8']}
                    style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 100001 }}
                />
            )}

            {/* Modales Legales Emergentes */}
            {showTermsModal && renderLegalModal(
                'Términos y Condiciones',
                config?.termsAndConditionsText || DEFAULT_TERMS,
                setShowTermsModal
            )}
            {showPrivacyModal && renderLegalModal(
                'Aviso de Privacidad',
                config?.privacyPolicyText || DEFAULT_PRIVACY,
                setShowPrivacyModal
            )}

            {/* Modal Resiliente de Recuperación de Cupones (Rate Limiting y Anti-Bot) */}
            {showRecoverModal && (
                <div
                    className="OptimoOpticasFan__RecoverModalBackdrop"
                    onClick={() => !recoverLoading && setShowRecoverModal(false)}
                >
                    <div
                        className="OptimoOpticasFan__RecoverModalCard"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                            <h4 className="fw-bold text-dark mb-0 fs-5 d-flex align-items-center">
                                <i className="bi bi-ticket-perforated-fill me-2" style={{ color: '#124dde' }}></i>
                                Recuperar mi cupón
                            </h4>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Cerrar"
                                disabled={recoverLoading}
                                onClick={() => setShowRecoverModal(false)}
                            ></button>
                        </div>

                        <p className="text-secondary small mb-3">
                            Ingresa el correo electrónico o número telefónico (10 dígitos) con el que te registraste para consultar y recuperar tu cupón de descuento:
                        </p>

                        {recoverError && (
                            <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3 fw-semibold">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                {recoverError}
                            </div>
                        )}

                        {cooldownSeconds > 0 && (
                            <div className="alert alert-warning py-2 px-3 small rounded-3 mb-3 fw-bold d-flex align-items-center">
                                <span className="spinner-grow spinner-grow-sm me-2" role="status"></span>
                                Espera {cooldownSeconds}s para reintentar la búsqueda.
                            </div>
                        )}

                        <form onSubmit={handleRecoverSubmit}>
                            {/* Honeypot anti-bot invisible */}
                            <input
                                type="text"
                                name="botTrap"
                                value={botTrap}
                                onChange={(e) => setBotTrap(e.target.value)}
                                style={{ display: 'none', position: 'absolute', left: '-9999px', opacity: 0 }}
                                tabIndex="-1"
                                autoComplete="off"
                                aria-hidden="true"
                            />

                            <div className="mb-3">
                                <input
                                    type="text"
                                    className="OptimoOpticasFan__Input"
                                    placeholder="tu.correo@ejemplo.com o 4441234567"
                                    value={recoverQuery}
                                    onChange={(e) => {
                                        setRecoverQuery(e.target.value);
                                        if (recoverError) setRecoverError('');
                                    }}
                                    disabled={recoverLoading || cooldownSeconds > 0}
                                    autoFocus
                                />
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary w-100 py-2.5 rounded-3 fw-bold text-uppercase d-flex align-items-center justify-content-center"
                                style={{ backgroundColor: '#124dde', borderColor: '#124dde' }}
                                disabled={recoverLoading || cooldownSeconds > 0 || !recoverQuery.trim()}
                            >
                                {recoverLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Consultando...
                                    </>
                                ) : cooldownSeconds > 0 ? (
                                    <>
                                        <i className="bi bi-hourglass-split me-1"></i>
                                        Espera {cooldownSeconds}s
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-search me-1"></i>
                                        Buscar mi Cupón
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* ================================================================= */}
            {/* 1. FASE DE REGISTRO (FORM)                                        */}
            {/* ================================================================= */}
            {phase === 'form' && (
                <div className="OptimoOpticasFan__Container">
                    {/* Barra superior de acceso rápido si ya existen cupones */}
                    {storedCoupons.length > 0 && (
                        <div className="mb-3 text-center d-flex flex-wrap justify-content-center gap-2 w-100">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light rounded-pill px-3 py-1 fw-bold shadow-sm"
                                style={{ fontSize: '0.8rem', letterSpacing: '0.04em' }}
                                onClick={() => {
                                    setActiveCoupon(storedCoupons[0]);
                                    setPhase('revealed');
                                }}
                            >
                                <i className="bi bi-ticket-perforated-fill me-1"></i> Ver mi cupón ganado ({storedCoupons.length})
                            </button>
                        </div>
                    )}

                    <div className="OptimoOpticasFan__Card">
                        {/* Header con logo blanco */}
                        <img
                            src="/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"
                            alt="Optimo Ópticas"
                            className="OptimoOpticasFan__Logo"
                            style={{ width: '100%', maxWidth: '250px' }}
                            onError={(e) => { e.target.src = "/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"; }}
                        />
                        <h2 className="OptimoOpticasFan__Title">¡REGÍSTRATE!</h2>
                        <p className="OptimoOpticasFan__Subtitle">
                            Escribe tus datos para obtener tu regalo de parte de<br />
                            <strong>OPTIMO ÓPTICAS</strong>
                        </p>

                                {validationErrors.general && (
                                    <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3 text-start">
                                        {validationErrors.general}
                                    </div>
                                )}

                                <form onSubmit={handleFormSubmit} noValidate>
                                    {/* Input: Nombre completo */}
                                    <div className="mb-3 text-start">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            onFocus={handleInputFocus}
                                            placeholder="Nombre completo"
                                            className={`OptimoOpticasFan__Input ${validationErrors.name ? 'is-invalid' : ''}`}
                                            autoComplete="name"
                                            required
                                        />
                                        {validationErrors.name && (
                                            <div className="text-danger small mt-1 ps-1">{validationErrors.name}</div>
                                        )}
                                    </div>

                                    {/* Input: Correo electrónico */}
                                    <div className="mb-3 text-start">
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            onFocus={handleInputFocus}
                                            placeholder="Correo electrónico"
                                            className={`OptimoOpticasFan__Input ${validationErrors.email ? 'is-invalid' : ''}`}
                                            autoComplete="email"
                                            required
                                        />
                                        {validationErrors.email && (
                                            <div className="text-danger small mt-1 ps-1">{validationErrors.email}</div>
                                        )}
                                    </div>

                                    {/* Input: Teléfono celular (10 dígitos) */}
                                    <div className="mb-3 text-start">
                                        <input
                                            type="tel"
                                            name="phone"
                                            maxLength="10"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            onFocus={handleInputFocus}
                                            placeholder="Teléfono (10 dígitos)"
                                            className={`OptimoOpticasFan__Input ${validationErrors.phone ? 'is-invalid' : ''}`}
                                            autoComplete="tel"
                                            required
                                        />
                                        {validationErrors.phone && (
                                            <div className="text-danger small mt-1 ps-1">{validationErrors.phone}</div>
                                        )}
                                    </div>

                                    {/* [LFPDPPP] Casilla 1 (Obligatoria): Términos y Aviso de Privacidad */}
                                    <div className="form-check text-start mb-2.5">
                                        <input
                                            type="checkbox"
                                            id="acceptedTermsCheck"
                                            name="acceptedTerms"
                                            checked={formData.acceptedTerms}
                                            onChange={handleInputChange}
                                            className={`form-check-input ${validationErrors.acceptedTerms ? 'is-invalid' : ''}`}
                                            style={{ cursor: 'pointer', borderColor: '#124dde' }}
                                        />
                                        <label
                                            htmlFor="acceptedTermsCheck"
                                            className="form-check-label small text-secondary"
                                            style={{ fontSize: '0.78rem', lineHeight: '1.4', cursor: 'pointer' }}
                                        >
                                            Acepto los{' '}
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 fw-bold text-decoration-underline align-baseline"
                                                style={{ fontSize: '0.78rem', color: '#124dde' }}
                                                onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                                            >
                                                Términos y Condiciones
                                            </button>
                                            {' '}y el{' '}
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 fw-bold text-decoration-underline align-baseline"
                                                style={{ fontSize: '0.78rem', color: '#124dde' }}
                                                onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                                            >
                                                Aviso de Privacidad
                                            </button>.
                                        </label>
                                        {validationErrors.acceptedTerms && (
                                            <div className="text-danger small mt-1 ps-1">{validationErrors.acceptedTerms}</div>
                                        )}
                                    </div>

                                    {/* [LFPDPPP] Casilla 2 (Opcional): Marketing Optimo Ópticas */}
                                    <div className="form-check text-start mb-4">
                                        <input
                                            type="checkbox"
                                            id="acceptedMarketingCheck"
                                            name="acceptedMarketing"
                                            checked={formData.acceptedMarketing}
                                            onChange={handleInputChange}
                                            className="form-check-input"
                                            style={{ cursor: 'pointer', borderColor: '#94a3b8' }}
                                        />
                                        <label
                                            htmlFor="acceptedMarketingCheck"
                                            className="form-check-label small text-secondary"
                                            style={{ fontSize: '0.78rem', lineHeight: '1.4', cursor: 'pointer' }}
                                        >
                                            Deseo recibir promociones y novedades exclusivas de Optimo Ópticas.
                                        </label>
                                    </div>

                                    {/* Botón 'CONTINUAR' */}
                                    <button
                                        type="submit"
                                        className="OptimoOpticasFan__BtnSubmit"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <span>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                PROCESANDO...
                                            </span>
                                        ) : (
                                            'CONTINUAR'
                                        )}
                                    </button>
                                </form>

                                {/* Enlace discreto de recuperación de cupón */}
                                <div className="text-center pt-3 mt-3 border-top border-secondary border-opacity-25">
                                    <button
                                        type="button"
                                        className="btn btn-link text-decoration-none small fw-bold p-0"
                                        style={{ color: '#124dde' }}
                                        onClick={() => {
                                            setRecoverError('');
                                            setRecoverQuery('');
                                            setShowRecoverModal(true);
                                        }}
                                    >
                                        <i className="bi bi-ticket-perforated-fill me-1"></i> ¿Ya participaste? Recupera tu cupón aquí
                                    </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================================================================= */}
            {/* PANTALLA: YA PARTICIPÓ PREVIAMENTE (NO GANADOR)                   */}
            {/* ================================================================= */}
            {phase === 'already_participated' && (
                <div className="OptimoOpticasFan__Container animate__animated animate__fadeIn">
                    <div className="OptimoOpticasFan__Card text-center py-4">
                        <img
                            src="/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"
                            alt="Optimo Ópticas"
                            className="OptimoOpticasFan__Logo mb-3"
                            style={{ width: '100%', maxWidth: '240px' }}
                            onError={(e) => { e.target.src = "/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"; }}
                        />

                        <div className="mb-3">
                            <span className="badge rounded-circle p-3 shadow-sm d-inline-flex justify-content-center align-items-center" 
                                  style={{ width: '70px', height: '70px', backgroundColor: '#e0e7ff', color: '#124dde' }}>
                                <i className="bi bi-patch-check-fill fs-1"></i>
                            </span>
                        </div>

                        <h2 className="OptimoOpticasFan__Title mb-2">¡Gracias por participar!</h2>

                        <p className="text-secondary mb-3 px-2" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Tu registro ya fue recibido anteriormente para esta dinámica.
                        </p>

                        <p className="text-muted mb-4 px-2 small" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                            Para garantizar la equidad, se permite únicamente una participación por aficionado. En caso de haber resultado ganador(a) de un cupón de descuento, puedes consultarlo con tu número de teléfono o correo electrónico.
                        </p>

                        <div className="d-flex flex-column gap-2 mb-2">
                            <button
                                type="button"
                                className="btn btn-outline-primary rounded-pill py-2.5 px-4 fw-bold d-flex align-items-center justify-content-center gap-2"
                                style={{ borderColor: '#124dde', color: '#124dde' }}
                                onClick={() => {
                                    setRecoverError('');
                                    setRecoverQuery(formData.phone || formData.email || '');
                                    setShowRecoverModal(true);
                                }}
                            >
                                <i className="bi bi-ticket-perforated-fill me-1"></i> Consultar mi cupón
                            </button>

                            <button
                                type="button"
                                className="btn btn-link text-secondary text-decoration-none small fw-semibold mt-1"
                                onClick={() => {
                                    setFormData({
                                        name: '',
                                        email: '',
                                        phone: '',
                                        acceptedTerms: false,
                                        acceptedMarketing: false
                                    });
                                    setValidationErrors({});
                                    setPhase('form');
                                }}
                            >
                                <i className="bi bi-arrow-left me-1"></i> Regresar al inicio
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================================================================= */}
            {/* 2. FASE DE RASPADO EN CANVAS TÁCTIL (SCRATCH)                      */}
            {/* ================================================================= */}
            {phase === 'scratch' && (
                <div className="OptimoOpticasFan__CardWrapper" ref={cardWrapperRef}>
                    {/* Tarjeta Subyacente del Cupón */}
                    <main
                        className={`OptimoOpticasFan__Card ${cardPulse ? 'scale-105' : ''}`}
                        id="pass-card"
                    >
                        <img
                            src="/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"
                            alt="Optimo Ópticas"
                            className="OptimoOpticasFan__LogoSm"
                            onError={(e) => { e.target.src = "/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"; }}
                        />
                        <h1 className="OptimoOpticasFan__Title">¡Felicidades!</h1>
                        <div className="OptimoOpticasFan__Divider"></div>

                        <div className="OptimoOpticasFan__DiscountBlock">
                            {/* <p>
                                Obtuviste un
                                <strong>20% DE DESCUENTO</strong>
                                {activeCoupon?.discountDescription && !activeCoupon.discountDescription.includes('20%')
                                    ? activeCoupon.discountDescription
                                    : 'en cualquier servicio.'}
                            </p> */}
                            <p>
                                ¡YA GANASTE!
                                <strong>2x1 en lentes Optimo</strong>
                                Tu premio te espera en Optimo Ópticas The Park. Ven y conoce los beneficios exclusivos que tenemos para ti.
                                {/* {activeCoupon?.discountDescription && !activeCoupon.discountDescription.includes('20%')
                                    ? activeCoupon.discountDescription
                                    : 'en cualquier servicio.'} */}
                            </p>
                        </div>

                        {/* Banner Shimmer con Folio */}
                        <div className="OptimoOpticasFan__BannerShimmer">
                            <div className="OptimoOpticasFan__ShimmerEffect"></div>
                            <h2>Cupón Exclusivo</h2>
                            <div className="OptimoOpticasFan__CouponCodeBadge">
                                {activeCoupon?.couponCode ? (activeCoupon.couponCode.startsWith('#') ? activeCoupon.couponCode : `#${activeCoupon.couponCode}`) : '#OPTI20'}
                            </div>
                        </div>

                        {/* Código QR si está activo */}
                        {showQr && (
                            <div id="scratch-pass-qr" className="d-flex justify-content-center my-3 mx-auto" style={{ width: 'fit-content' }}>
                                <div className="OptimoOpticasFan__QrContainer">
                                    <div className="OptimoOpticasFan__QrInner">
                                        <QRCodeSVG
                                            value={activeCoupon?.verificationToken || activeCoupon?.couponCode || 'OPTI20'}
                                            size={120}
                                            level="H"
                                            imageSettings={{
                                                src: "/img/optimoopticas/logo-optimo-opticas-primario-cuadrado.png",
                                                height: 30,
                                                width: 30,
                                                excavate: true
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Datos personales enmascarados si showUserData está activo */}
                        {showUserData && (
                            <div className="OptimoOpticasFan__UserDataBox my-2">
                                <span className="fw-bold text-dark d-block">{maskedUserData.name || 'Aficionado Registrado'}</span>
                                <span className="text-secondary small d-block" style={{ fontSize: '0.8rem' }}>
                                    {maskedUserData.email}{maskedUserData.phone ? ` | ${maskedUserData.phone}` : ''}
                                </span>
                            </div>
                        )}

                        {/* Leyenda de canje en sucursal si showLocationNote está activo */}
                        {showLocationNote && (
                            <div className="OptimoOpticasFan__NoticeBox">
                                <p>{activeCoupon?.additionalInstructions || config?.additionalInstructions || 'MUESTRA ESTE CUPÓN EN SUCURSAL PARA HACERLO VÁLIDO'}</p>
                            </div>
                        )}
                    </main>

                    {/* Capa de Niebla Difusa */}
                    <div
                        ref={fogRef}
                        className="OptimoOpticasFan__FogOverlay"
                        id="fog-overlay"
                    />

                    {/* Lienzo Canvas de Rasca y Gana */}
                    <canvas
                        ref={canvasRef}
                        className="OptimoOpticasFan__Canvas"
                        id="scratch-canvas"
                        style={{ touchAction: 'none' }}
                    />
                </div>
            )}

            {/* ================================================================= */}
            {/* 3. VISTA DE CUPÓN REVELADO (REVEALED)                             */}
            {/* ================================================================= */}
            {phase === 'revealed' && (
                <div className="OptimoOpticasFan__Container">
                    {/* Selector Horizontal Multi-Cupón (si storedCoupons.length > 1) */}
                    {storedCoupons.length > 1 && (
                        <div className="OptimoOpticasFan__CouponPills mb-3 w-100">
                            <div className="d-flex align-items-center gap-2 overflow-auto pb-1 px-1" style={{ WebkitOverflowScrolling: 'touch' }}>
                                {storedCoupons.map((coupon, idx) => {
                                    const isSelected = (activeCoupon?.couponCode || '') === (coupon.couponCode || '');
                                    const cleanCode = coupon.couponCode ? (coupon.couponCode.startsWith('#') ? coupon.couponCode : `#${coupon.couponCode}`) : `#${idx + 1}`;
                                    return (
                                        <button
                                            key={coupon.couponCode || idx}
                                            type="button"
                                            className={`OptimoOpticasFan__CouponPill ${isSelected ? 'active' : ''}`}
                                            onClick={() => setActiveCoupon(coupon)}
                                        >
                                            <i className="bi bi-ticket-perforated-fill me-1"></i>
                                            Cupón {idx + 1} ({cleanCode})
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* BOTÓN SUPERIOR DE GUARDAR CUPÓN EN GALERÍA */}
                    <div className="text-center mb-3 mx-auto w-100" style={{ maxWidth: '440px' }}>
                        <p className="small mb-2" style={{ fontSize: '0.78rem', color: '#c6c5c5' }}>
                            Aquí está tu cupón de regalo. Guárdalo o toma captura de pantalla
                        </p>
                        <button
                            type="button"
                            className="OptimoOpticasFan__BtnSaveGallery w-100 py-3 rounded-3 fw-bold text-uppercase shadow-lg d-flex align-items-center justify-content-center gap-2"
                            onClick={handleSavePassToGallery}
                            disabled={isSavingImage}
                        >
                            {isSavingImage ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-download me-1 fs-5"></i>
                                    Guardar Cupón en mi Galería
                                </>
                            )}
                        </button>
                    </div>

                    <main className="OptimoOpticasFan__Card shadow-2xl" id="pass-card">
                        <img
                            src="/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"
                            alt="Optimo Ópticas"
                            className="OptimoOpticasFan__LogoSm"
                            onError={(e) => { e.target.src = "/img/optimoopticas/logo-optimo-opticas-secundario-rectangulo.png"; }}
                        />
                        <h1 className="OptimoOpticasFan__Title">¡Felicidades!</h1>
                        <div className="OptimoOpticasFan__Divider"></div>

                        <div className="OptimoOpticasFan__DiscountBlock">
                            {/* <p>
                                Obtuviste un
                                <strong>20% DE DESCUENTO</strong>
                                {activeCoupon?.discountDescription && !activeCoupon.discountDescription.includes('20%')
                                    ? activeCoupon.discountDescription
                                    : 'en cualquier servicio.'}
                            </p> */}
                            <p>
                                ¡YA GANASTE!
                                <strong>2x1 en lentes Optimo</strong>
                                Tu premio te espera en Optimo Ópticas The Park. Ven y conoce los beneficios exclusivos que tenemos para ti.
                                {/* {activeCoupon?.discountDescription && !activeCoupon.discountDescription.includes('20%')
                                    ? activeCoupon.discountDescription
                                    : 'en cualquier servicio.'} */}
                            </p>
                        </div>

                        {/* Banner Shimmer con Folio */}
                        <div className="OptimoOpticasFan__BannerShimmer">
                            <div className="OptimoOpticasFan__ShimmerEffect"></div>
                            <h2>Cupón Exclusivo</h2>
                            <div className="OptimoOpticasFan__CouponCodeBadge">
                                {activeCoupon?.couponCode ? (activeCoupon.couponCode.startsWith('#') ? activeCoupon.couponCode : `#${activeCoupon.couponCode}`) : '#OPTI20'}
                            </div>
                        </div>

                        <span className="text-secondary small mt-1" style={{ fontSize: '0.72rem' }}>
                            Presenta este cupón en caja
                        </span>

                        {/* Código QR Verificable si showQr está activo */}
                        {showQr && (
                            <div className="d-flex flex-column align-items-center justify-content-center my-3">
                                <div id="coupon-qr-container" className="OptimoOpticasFan__QrContainer">
                                    <div className="OptimoOpticasFan__QrInner">
                                        <QRCodeSVG
                                            value={activeCoupon?.verificationToken || activeCoupon?.couponCode || 'OPTI20'}
                                            size={150}
                                            level="H"
                                            imageSettings={{
                                                src: "/img/optimoopticas/logo-optimo-opticas-primario-cuadrado.png",
                                                height: 38,
                                                width: 38,
                                                excavate: true
                                            }}
                                        />
                                    </div>
                                </div>
                                {/* <span className="text-secondary small mt-1" style={{ fontSize: '0.72rem' }}>
                                    Presenta este cupón en caja
                                </span> */}
                            </div>
                        )}

                        {/* Datos personales enmascarados si showUserData está activo */}
                        {showUserData && (
                            <div className="OptimoOpticasFan__UserDataBox my-2">
                                <span className="fw-bold text-dark d-block">{maskedUserData.name || 'Aficionado Registrado'}</span>
                                <span className="text-secondary small d-block" style={{ fontSize: '0.8rem' }}>
                                    {maskedUserData.email}{maskedUserData.phone ? ` | ${maskedUserData.phone}` : ''}
                                </span>
                            </div>
                        )}

                        {/* Leyenda de canje en sucursal si showLocationNote está activo */}
                        {showLocationNote && (
                            <div className="OptimoOpticasFan__NoticeBox">
                                <p>{activeCoupon?.additionalInstructions || config?.additionalInstructions || 'MUESTRA ESTE CUPÓN EN SUCURSAL PARA HACERLO VÁLIDO'}</p>
                            </div>
                        )}
                    </main>

                    {/* BOTÓN INFERIOR DE GUARDAR CUPÓN EN GALERÍA */}
                    <div className="text-center mt-3 mx-auto w-100" style={{ maxWidth: '440px' }}>
                        <button
                            type="button"
                            className="OptimoOpticasFan__BtnSaveGallery w-100 py-3 rounded-3 fw-bold text-uppercase shadow-lg d-flex align-items-center justify-content-center gap-2"
                            onClick={handleSavePassToGallery}
                            disabled={isSavingImage}
                        >
                            {isSavingImage ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-download me-1 fs-5"></i>
                                    Guardar Cupón en mi Galería
                                </>
                            )}
                        </button>
                        <p className="small mt-2 mb-0" style={{ fontSize: '0.78rem', color: '#c6c5c5' }}>
                            Aquí está tu cupón de regalo. Guárdalo o toma captura de pantalla
                        </p>

                        {/* Enlace discreto para recuperar otros cupones registrados */}
                        {/* <div className="pt-3">
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-white text-opacity-75 text-decoration-none"
                                style={{ fontSize: '0.8rem' }}
                                onClick={() => {
                                    setRecoverError('');
                                    setRecoverQuery('');
                                    setShowRecoverModal(true);
                                }}
                            >
                                <i className="bi bi-ticket-perforated me-1"></i> ¿Tienes otro cupón registrado? Recupéralo aquí
                            </button>
                        </div> */}
                    </div>
                </div>
            )}

            {/* Pie de página con créditos de Interaktia y parámetros UTM */}
            <footer className="OptimoOpticasFan__Footer">
                <p className="FdmFooter FooterCredits mb-0">
                    <a
                        href="https://interaktia.com/?utm_source=optimo_opticas_web&utm_medium=footer&utm_campaign=fdm_adsl"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Hecho con ♥ por Interaktia
                    </a>
                </p>
            </footer>
        </div>
    );
};

export default OptimoOpticasFan;